// authStore — Auth0 permissions bridge.
// Full implementation lands in plan 07-03 (Pinia stores + types refinement).
// This skeleton is referenced by the route guard in router/index.ts.
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export const useAuthStore = defineStore('auth', () => {
    const permissions = ref<string[]>([])
    const loaded = ref(false)

    function setPermissions(perms: string[]): void {
        permissions.value = perms
        loaded.value = true
    }

    function hasPermission(perm: string): boolean {
        return permissions.value.includes(perm)
    }

    /**
     * ensurePermissionsLoaded — guarantees permissions[] is populated from the
     * Auth0 access-token's `permissions` claim before resolving. Idempotent.
     * Called by the route guard on every protected navigation.
     */
    async function ensurePermissionsLoaded(
        getAccessTokenSilently?: () => Promise<string>,
    ): Promise<void> {
        if (loaded.value) return
        if (!getAccessTokenSilently) return
        try {
            const token = await getAccessTokenSilently()
            const payloadB64 = token.split('.')[1]
            if (!payloadB64) return
            const payload = JSON.parse(atob(payloadB64)) as { permissions?: unknown }
            if (Array.isArray(payload.permissions)) {
                setPermissions(payload.permissions as string[])
            }
        } catch {
            // Token unavailable; authGuard will redirect to login
        }
    }

    const canRead = computed(() => hasPermission('futures:read'))
    const canWrite = computed(() => hasPermission('futures:write'))
    const canApprove = computed(() => hasPermission('futures:approve'))

    return {
        permissions,
        loaded,
        canRead,
        canWrite,
        canApprove,
        hasPermission,
        setPermissions,
        ensurePermissionsLoaded,
    }
})
